import { useEffect, useState } from 'react';
import { Menu, useSidebarState, useStore } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { Box, Collapse, InputAdornment, TextField, Typography, ButtonBase } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { adminBrand, navigationGroups, overview, type NavigationGroup, type NavigationItem } from '../config/navigation';
import { useAdminText } from './shared/useAdminText';

function NavLink({ item }: { item: NavigationItem }) {
  const t = useAdminText();
  return <Menu.Item to={item.path} primaryText={t(`navigation.${item.id}`, item.label)} leftIcon={<item.icon />} />;
}

function Group({ group, rail, search }: { group: NavigationGroup; rail: boolean; search: string }) {
  const { pathname } = useLocation();
  const t = useAdminText();
  const active = group.items.some(item => pathname === item.path || pathname.startsWith(item.path + '/'));
  const [expanded, setExpanded] = useStore(`admin.navigation.${group.id}`, group.id === 'people' || active);
  useEffect(() => { if (active) setExpanded(true); }, [active, setExpanded]);
  const groupLabel = t(`groups.${group.id}`, group.label);
  const items = group.items.filter(item => `${groupLabel} ${t(`navigation.${item.id}`, item.label)}`.toLocaleLowerCase().includes(search));
  if (!items.length) return null;
  return <Box sx={{ mb: 0.75 }}>
    {!rail && <ButtonBase onClick={() => setExpanded(!expanded)} aria-expanded={!!search || expanded}
      aria-controls={`nav-${group.id}`} sx={{ width: '100%', justifyContent: 'space-between', px: 2, py: 1.25, borderRadius: 2, color: 'text.secondary' }}>
      <Typography variant="overline" sx={{ fontSize: 10.5, fontWeight: 750, letterSpacing: '0.09em' }}>{groupLabel}</Typography>
      <ExpandMoreIcon sx={{ fontSize: 17, transform: expanded || search ? 'rotate(180deg)' : undefined }} />
    </ButtonBase>}
    <Collapse in={rail || !!search || expanded} timeout="auto" unmountOnExit>
      <Box id={`nav-${group.id}`}>{items.map(item => <NavLink key={item.id} item={item} />)}</Box>
    </Collapse>
  </Box>;
}

export function CustomMenu() {
  const [open] = useSidebarState();
  const [search, setSearch] = useState('');
  const t = useAdminText();
  const query = open ? search.trim().toLocaleLowerCase() : '';
  const hasResults = navigationGroups.some(group => group.items.some(item =>
    `${t(`groups.${group.id}`, group.label)} ${t(`navigation.${item.id}`, item.label)}`.toLocaleLowerCase().includes(query)));
  return <Menu sx={{ mt: 0, px: 1, width: '100%' }}>
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', px: 1.25, py: 2.5 }}>
      <Box aria-hidden sx={{ flexShrink: 0, width: 34, height: 34, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2.5, fontWeight: 800 }}>{adminBrand.monogram}</Box>
      {open && <Box><Typography fontWeight={800}>{adminBrand.name}</Typography><Typography variant="caption" color="text.secondary">{t('workspace', adminBrand.workspace)}</Typography></Box>}
    </Box>
    {open && <TextField value={search} onChange={event => setSearch(event.target.value)} size="small"
      placeholder={t('searchNavigation', 'Find a page…')} inputProps={{ 'aria-label': t('searchNavigation', 'Find a page…') }}
      onKeyDown={event => { if (event.key === 'Escape') { setSearch(''); event.stopPropagation(); } }}
      slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
      sx={{ mx: 0.5, mb: 2, width: 'calc(100% - 8px)' }} />}
    {!query && <Box sx={{ mb: 1 }}><NavLink item={overview} /></Box>}
    {navigationGroups.map(group => <Group key={group.id} group={group} rail={!open} search={query} />)}
    {!hasResults && <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('noPages', 'No matching pages.')}</Typography>}
  </Menu>;
}
